<template>
  <div class="w-full flex justify-between mt-16 gap-32 flex-nowrap">
    <header class="flex flex-col gap-8 items-start max-w-[700px]">
      <OBadgeStatus
        v-if="pergunta.tag"
        :color="pergunta.tag_cor_in_rgb_readonly"
        square
        size="md"
        class="h-24"
      >
        <template #content>
          <p
            class="text-caps-2 text-center mx-auto pointer-events-none"
          >
            {{ pergunta.tag_nome_readonly }}
          </p>
        </template>
      </OBadgeStatus>
      <div class="flex items-center gap-8 flex-nowrap">
        <h1 class="text-title-3 text-neutral-100">
          {{ title }}
        </h1>
        <div
          v-if="assessment?.exibir_pontuacao"
          class="size-28 cursor-pointer bg-neutral-100/5 rounded-lg flex-shrink-0"
        >
          <IconCoin />
          <q-tooltip v-bind="tooltipProps">
            {{ t('maxScoreValue') }}:
            {{ pergunta.pontuacao_traduzida || 0 }}
          </q-tooltip>
        </div>
      </div>

      <p class="text-paragraph-3 text-neutral-70">
        {{ description }}
      </p>
    </header>

    <div class="flex flex-col items-end gap-24">
      <OSelectPaginated
        v-if="isAdmin || isCorporateGroup"
        :model-value="fabricante"
        :api-url="`api/fabricante/fabricantes_superuser/`"
        :adictional-filters="{
          assessment: context.assessment?.id,
          pergunta: 'True',
        }"
        :label="t('selectBottler')"
        option-label="nome_fabricante"
        option-value="id"
        class="min-w-[335px] bg-white"
        menu-anchor="bottom start"
        menu-self="top start"
        :show-search-input="true"
        search-placeholder="Pesquisar fabricante..."
        @update:model-value="onChangeSessionFabricante"
      />

      <div class="flex items-center gap-8 flex-nowrap">
        <OButton
          v-if="showEvidences && tipoEvidencia?.alternativas"
          primary
          size="lg"
          :label="t('evidences')"
          :disable="isDisabledButtonEvidences"
          class="text-headline-2 !text-base"
          @click="emit('click:open-evidencias', { sendAll: false })"
        />
        <OButton
          v-else-if="showEvidences && tipoEvidencia?.pergunta"
          primary
          size="lg"
          icon-size="1.5rem"
          :disable="isDisabledButtonEvidences"
          class="text-headline-2 !text-base"
          @click="emit('click:open-evidencias', { sendAll: true })"
        >
          <div
            :style="{ background: styleIcon.background }"
            class="size-28 grid place-items-center rounded-[32px] bg-white border border-neutral-100/10 !p-0 overflow-hidden"
          >
            <q-icon
              name="svguse:#icon_anexo"
              :style="{ color: styleIcon.color }"
            />
          </div>
          {{ textEvidences }}
        </OButton>
        <OButton
          v-if="assessment?.exibir_plano_acao"
          primary
          size="lg"
          :label="t('futurePlan')"
          :disable="isDisabledButtonPlanoAcao"
          class="text-headline-2 !text-base"
          @click="emit('click:open-plano-acao')"
        />
        <OButton
          v-if="showSummaryAdmin"
          primary
          size="lg"
          :label="t('summary')"
          :disable="!pergunta.acompanhamento"
          class="text-headline-2 !text-base"
          @click="emit('click:open-acompanhamento')"
        />
        <OButton
          v-else-if="assessment?.exibir_acompanhamento"
          primary
          size="lg"
          :href="urlAcompanhamento"
          :label="t('summary')"
          class="text-headline-2 !text-base"
        />
        <template v-if="isAdmin">
          <div class="h-20 w-2 mx-24 bg-neutral-100/10" />
          <OButton
            secondary
            size="lg"
            :label="t('editFields')"
            icon-size="1.5rem"
            icon="svguse:#icon_pen-edit"
            class="text-headline-2 !text-base"
            @click="emit('click:open-editar-campos')"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import {
  setBgStatus,
  setColorStatus,
  statusEvidencia,
  tooltipProps,
} from 'js/utils/GLOBAL'
import { useI18n } from 'vue-i18n'
import IconCoin from 'public/icons/coin.svg'
import OBadgeStatus from 'components/Badge/OBadgeStatus.vue'
import OButton from 'components/Button/OButton.vue'
import OSelectPaginated from 'components/Select/OSelectPaginated.vue'
import queryString from 'query-string'
import usePerguntas from 'composables/usePerguntas'

const { t } = useI18n()
const {
  planoAcao,
  isTabPrevious,
  isCorporateGroup,
  tipoEvidencia,
  hasEvidencia,
} = usePerguntas()

const context = inject('context')
const user = context.user

const { pergunta, assessment } = defineProps({
  title: String,
  description: String,
  fabricante: Object,
  pergunta: Object,
  assessment: Object,
})

const emit = defineEmits([
  'update:fabricante',
  'click:open-evidencias',
  'click:open-plano-acao',
  'click:open-editar-campos',
  'click:open-acompanhamento',
])

const urlAcompanhamento = ref(
  `/${context.assessment.slug}/acompanhamento_detalhes/?pergunta=${pergunta?.id}`
)

const showEvidences = computed(() => {
  return assessment?.exibir_evidencia && pergunta?.necessita_evidencia
})

const showSummaryAdmin = computed(() => {
  return (
    assessment?.exibir_acompanhamento &&
    isAdmin.value &&
    !isCorporateGroup.value
  )
})

const isAdmin = computed(() => {
  return user.superuser || user.staff
})

const isDisabledButtonEvidences = computed(() => {
  const isEvidence = pergunta.alternativa_set.find((alt) => {
    return alt.tem_evidencia
  })
  if (!pergunta.pode_enviar_evidencia && !isEvidence) {
    return true
  }
  if (isTabPrevious.value && !isEvidence) {
    return true
  }
  if ((isAdmin.value || isCorporateGroup.value) && !isEvidence) {
    return true
  }
  return false
})

const isDisabledButtonPlanoAcao = computed(() => {
  return (
    (user.grupo_corporativo || isTabPrevious.value) &&
    !planoAcao.value
  )
})

const textEvidences = computed(() => {
  const status = statusEvidencia(pergunta.status_evidencia_fabricante)

  if (!pergunta.status_evidencia_fabricante)
    return t('sendAllEvidences')
  if (status?.reprovada) return t('resendEvidences')
  return t('evidences')
})

const styleIcon = computed(() => {
  return {
    color: pergunta.status_evidencia_fabricante
      ? `rgba(${setColorStatus(pergunta.status_evidencia_fabricante)},1)`
      : `rgba(var(--icon-100),1) !important`,
    background: `rgba(${setBgStatus(pergunta.status_evidencia_fabricante)},1) !important`,
  }
})

function onChangeSessionFabricante(opt) {
  window.sessionStorage.setItem('id_fabricante', opt.id)
  const parsed = queryString.parse(window.location.search)
  const stringified = queryString.stringify({
    ...parsed,
    fabricantes: opt.id,
  })
  window.location.search = stringified
}
</script>

<style lang="scss" scoped></style>
